import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/DataTable";
import { motion } from "framer-motion";
import { CompanyClientResponse, OrderListItem } from "../../types/api.types";
import { formatIsoDateToUsSlash } from "../../utils/date";
import { stockService } from "../../services/stock/stockService";
import FilterOrders, {
  FilterOrdersFormData,
} from "../../components/FilterOrders";
import { useSnackbar } from "../../context/SnackbarContext";
import { orderService } from "../../services/order/orderService";
import { COMPANY_CHANGED_EVENT } from "../../utils/constants";

const ROWS_PER_PAGE = 5;

const OrdersPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchFilters, setSearchFilters] =
    useState<FilterOrdersFormData | null>(null);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [companyKey, setCompanyKey] = useState(0);

  const getClientId = (): string | undefined => {
    const company = JSON.parse(
      localStorage.getItem("company") || "{}",
    ) as CompanyClientResponse;
    return company?.clientID;
  };

  const getOrders = useCallback(
    async (filters?: FilterOrdersFormData | null) => {
      const clientId = getClientId();
      if (!clientId) {
        showSnackbar("No se encontró el cliente en sesión", "error", 4000);
        setOrders([]);
        setTotal(0);
        return;
      }

      setIsLoading(true);
      try {
        const dateFrom =
          filters?.dateFrom && filters?.dateTo
            ? formatIsoDateToUsSlash(filters.dateFrom)
            : undefined;
        const dateTo =
          filters?.dateFrom && filters?.dateTo
            ? formatIsoDateToUsSlash(filters.dateTo)
            : undefined;

        const response = await orderService.getOrders(
          currentPage,
          ROWS_PER_PAGE,
          clientId,
          dateFrom,
          dateTo,
        );
        setOrders(response.orders);
        setTotal(response.total);
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        showSnackbar(
          err.response?.data?.message || "Error al cargar los pedidos",
          "error",
          4000,
        );
        setOrders([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, showSnackbar],
  );

  useEffect(() => {
    getOrders(searchFilters);
  }, [currentPage, searchFilters, getOrders, companyKey]);

  useEffect(() => {
    const onCompanyChanged = () => {
      setCurrentPage(0);
      setCompanyKey((k) => k + 1);
    };
    window.addEventListener(COMPANY_CHANGED_EVENT, onCompanyChanged);
    return () =>
      window.removeEventListener(COMPANY_CHANGED_EVENT, onCompanyChanged);
  }, []);

  const handleFilter = (data: FilterOrdersFormData) => {
    setSearchFilters(data);
    setCurrentPage(0);
  };

  const handleCancelFilter = () => {
    setSearchFilters(null);
    setCurrentPage(0);
  };

  const columns = [
    {
      header: "Nro. de orden",
      key: "orderNumber",
      render: (row: OrderListItem) => (
        <Typography>{row.consecutiveNumber ?? row.orderId}</Typography>
      ),
    },
    {
      header: "Estado",
      key: "status",
    },
    {
      header: "Fecha creación",
      key: "createDate",
    },
    {
      header: "Total",
      key: "total",
      render: (row: OrderListItem) => (
        <Typography>
          {Number(row.total).toLocaleString("es-AR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>
      ),
    },
    {
      header: "Acciones",
      key: "actions",
      render: (row: OrderListItem) => {
        const cartUrl = `/orders/create?orderId=${encodeURIComponent(row.orderId)}`;
        if (row.status === "SENT") {
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              <VisibilityIcon
                sx={{ cursor: "pointer", color: "primary.main" }}
                onClick={() => navigate(cartUrl)}
                titleAccess="Ver pedido"
              />
            </Box>
          );
        }
        if (row.status === "DRAFT") {
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              <EditIcon
                sx={{ cursor: "pointer", color: "primary.main" }}
                onClick={() => navigate(cartUrl)}
                titleAccess="Editar borrador"
              />
            </Box>
          );
        }
        return null;
      },
    },
  ];

  const onGetOrderId = async () => {
    const clientId = getClientId();
    if (!clientId) {
      showSnackbar("No se encontró el cliente en sesión", "error", 4000);
      return;
    }
    const response = await stockService.getOrderId(clientId);
    if (response) {
      navigate(`/orders/create?orderId=${encodeURIComponent(response)}`);
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h4">Pedidos</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  onGetOrderId();
                }}
              >
                Cargar Pedido
              </Button>
            </Box>
          </motion.div>

          <Divider sx={{ my: 3 }} />

          <FilterOrders
            searchFilters={searchFilters}
            onFilter={handleFilter}
            onCancel={handleCancelFilter}
          />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            style={{ marginTop: 16 }}
          >
            <DataTable
              isLoading={isLoading}
              columns={columns}
              total={total}
              data={orders}
              page={currentPage + 1}
              rowsPerPage={ROWS_PER_PAGE}
              onChange={(page) => {
                setCurrentPage(page - 1);
              }}
            />
          </motion.div>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdersPage;
